import { useCallback, useEffect, useState } from "react";

import { AccountInfo } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useNavigate } from "@tanstack/react-router";
import { useDispatch } from "react-redux";

import { loginRequest, logoutRequest } from "../authConfig";
import { AUTH_ERRORS, DEFAULT_PROTECTED_ROUTE } from "../constants/auth";
import { store } from "../store";
import { hasValidAppToken } from "../utils/appToken";
import {
  deleteAccessTokenCookie,
  hasValidToken,
  setAccessTokenCookie,
} from "../utils/cookies";
import { showError } from "../utils/notification";
import { authDebugError } from "../utils/safeAuthLog";

interface UseAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  logoutWithNavigation: () => void;
  user: AccountInfo | null;
}

/**
 * Custom hook for authentication management
 * Simplifies auth operations and provides a clean interface
 */
export const useAuth = (): UseAuthReturn => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const dispatch = useDispatch<typeof store.dispatch>();

  const [isLoading, setIsLoading] = useState(false);

  // Get the current user from accounts
  const user = accounts.length > 0 ? accounts[0] : null;

  // Login function
  const login = useCallback(async () => {
    if (isLoading || isAuthenticated) return;

    setIsLoading(true);
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      authDebugError("useAuth: login error", error);
      showError(AUTH_ERRORS.LOGIN_FAILED, dispatch);
      setIsLoading(false);
    }
  }, [instance, isLoading, isAuthenticated, dispatch]);

  // Logout function (direct logout)
  const logout = useCallback(async () => {
    if (isLoading) return;

    setIsLoading(true);
    // External (email-code) users have no Microsoft account — decide BEFORE we
    // clear the token. They must NOT be sent to the Microsoft logout page.
    const isExternal = hasValidAppToken() || accounts.length === 0;
    try {
      // Clear tokens and local data
      deleteAccessTokenCookie();

      // Clear MSAL cache
      sessionStorage.clear();

      // Clear auth-related localStorage
      const localStorageKeys = Object.keys(localStorage);
      localStorageKeys.forEach((key) => {
        if (
          key.startsWith("msal.") ||
          key.includes("login") ||
          key.includes("auth")
        ) {
          localStorage.removeItem(key);
        }
      });

      if (isExternal) {
        // Also clear any cached MSAL account so a leftover Microsoft SSO session
        // in this browser doesn't silently auto-log the user back in — but do
        // NOT navigate to the Microsoft logout page (onRedirectNavigate:false
        // cancels the redirect while still clearing local MSAL state).
        try {
          await instance.logoutRedirect({
            onRedirectNavigate: () => false,
            postLogoutRedirectUri: window.location.origin + "/",
          });
        } catch (e) {
          authDebugError("useAuth: external MSAL cache clear failed", e);
        }
        window.location.href = "/";
        return;
      }

      await instance.logoutRedirect(logoutRequest);
    } catch (error) {
      authDebugError("useAuth: logout error", error);
      setIsLoading(false);
      if (isExternal) window.location.href = "/";
    }
  }, [instance, isLoading, accounts]);

  // Logout with navigation (navigate to signing out page)
  const logoutWithNavigation = useCallback(() => {
    navigate({ to: "/signing-out" });
  }, [navigate]);

  // Handle redirect after login
  useEffect(() => {
    const handleRedirect = async () => {
      try {
        // Always handle redirect promise - MSAL will return null if no redirect is pending
        // This is safe to call multiple times and must be called to complete the login flow
        const response = await instance.handleRedirectPromise();

        if (response?.account) {
          // User just completed login, set active account
          instance.setActiveAccount(response.account);
        }

        if (response?.accessToken) {
          setAccessTokenCookie(response.accessToken);
        }
      } catch (error) {
        authDebugError("useAuth: handle redirect error", error);

        // Check if this is a state not found error
        if ((error as any)?.errorCode === "state_not_found") {
          // Don't clear auth state or redirect - user might be on landing page legitimately
          // Just log and continue
        } else {
          // Only show error for actual authentication failures
          authDebugError("useAuth: authentication redirect error", error);
          // Don't automatically redirect - let the user stay on the current page
        }
      }
    };

    handleRedirect();
  }, [instance, dispatch, navigate]);

  // Auto-redirect authenticated users
  useEffect(() => {
    if (isAuthenticated && hasValidToken()) {
      const currentPath = window.location.pathname;
      if (currentPath === "/" || currentPath === "/auth-callback") {
        navigate({ to: DEFAULT_PROTECTED_ROUTE, replace: true });
      }
    }
  }, [isAuthenticated, navigate]);

  return {
    isAuthenticated,
    isLoading,
    login,
    logout,
    logoutWithNavigation,
    user,
  };
};
